import React, { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { getAllTeamsInLeague, getMyteamPlayer, getMyTeamGoalies } from '../../lib/supabase';
import { getProtectionList } from '../../lib/api/protectionList';
import toast from 'react-hot-toast';

interface Team {
  users_user_id: number;
  username: string;
}

interface Player {
  playerid: number;
  skaterfullname: string;
  positioncode: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  goals: number;
  assists: number;
  points: number;
  isnhl: boolean;
}

interface Goalie {
  playerid: number;
  goaliefullname: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  wins: number;
  losses: number;
  shutouts: number;
  savepct: number;
  goals: number;
  assists: number;
  isnhl: boolean;
}

interface OtherDGsProtectionTabProps {
  leagueId: number;
  currentUserId: number;
  language: 'en' | 'fr';
}

type PlayerType = 'players' | 'goalies';

export function OtherDGsProtectionTab({ leagueId, currentUserId, language }: OtherDGsProtectionTabProps) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedDgId, setSelectedDgId] = useState<number | null>(null);
  const [dgPlayers, setDgPlayers] = useState<Player[]>([]);
  const [dgGoalies, setDgGoalies] = useState<Goalie[]>([]);
  const [dgProtectedIds, setDgProtectedIds] = useState<number[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingDg, setLoadingDg] = useState(false);
  const [playerType, setPlayerType] = useState<PlayerType>('players');

  useEffect(() => {
    async function loadTeams() {
      try {
        const { data, error } = await getAllTeamsInLeague(leagueId);
        if (error) throw error;
        const others: Team[] = (data || [])
          .filter((t: any) => t.users_user_id !== currentUserId)
          .map((t: any) => ({ users_user_id: t.users_user_id, username: t.username }));
        setTeams(others);
        if (others.length > 0) {
          setSelectedDgId(others[0].users_user_id);
        }
      } catch (err) {
        console.error('Error loading teams:', err);
        toast.error('Erreur lors du chargement des équipes');
      } finally {
        setLoadingTeams(false);
      }
    }
    loadTeams();
  }, [leagueId, currentUserId]);

  useEffect(() => {
    if (selectedDgId === null) return;

    async function loadDgData() {
      setLoadingDg(true);
      try {
        const [playersResult, goaliesResult, protectionResult] = await Promise.all([
          getMyteamPlayer(leagueId, selectedDgId!),
          getMyTeamGoalies(leagueId, selectedDgId!),
          getProtectionList(selectedDgId!, leagueId),
        ]);

        if (playersResult.error) throw playersResult.error;
        if (goaliesResult.error) throw goaliesResult.error;
        if (protectionResult.error) throw protectionResult.error;

        setDgPlayers(playersResult.data || []);
        setDgGoalies(goaliesResult.data || []);
        setDgProtectedIds(protectionResult.data);
      } catch (err) {
        console.error('Error loading DG protection list:', err);
        toast.error('Erreur lors du chargement de la liste de protection');
      } finally {
        setLoadingDg(false);
      }
    }

    loadDgData();
  }, [selectedDgId, leagueId]);

  const protectedPlayers = dgPlayers.filter(p => dgProtectedIds.includes(p.playerid));
  const protectedGoalies = dgGoalies.filter(g => dgProtectedIds.includes(g.playerid));

  if (loadingTeams) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="text-center p-5">
        <p className="text-muted">Aucun autre directeur général dans cette ligue.</p>
      </div>
    );
  }

  return (
    <div>
      {/* DG selector */}
      <div className="mb-4">
        <label className="form-label fw-semibold">Sélectionner un directeur général</label>
        <select
          className="form-select"
          value={selectedDgId ?? ''}
          onChange={e => {
            setSelectedDgId(Number(e.target.value));
            setPlayerType('players');
          }}
        >
          {teams.map(team => (
            <option key={team.users_user_id} value={team.users_user_id}>
              {team.username}
            </option>
          ))}
        </select>
      </div>

      {/* Player Type Pills */}
      <ul className="nav nav-pills mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${playerType === 'players' ? 'active' : ''}`}
            onClick={() => setPlayerType('players')}
          >
            <Users size={18} className="me-2" />
            Joueurs
          </button>
        </li>
        <li className="nav-item ms-2">
          <button
            className={`nav-link ${playerType === 'goalies' ? 'active' : ''}`}
            onClick={() => setPlayerType('goalies')}
          >
            <Users size={18} className="me-2" />
            Gardiens
          </button>
        </li>
      </ul>

      {loadingDg ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </div>
      ) : (
        <>
          {playerType === 'players' && (
            protectedPlayers.length === 0 ? (
              <div className="text-center p-5">
                <p className="text-muted">Ce directeur général n'a aucun joueur protégé.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Pos</th>
                      <th>Équipe</th>
                      <th>Salaire</th>
                      <th>Années</th>
                      <th>PJ</th>
                      <th>B</th>
                      <th>P</th>
                      <th>Pts</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {protectedPlayers.map(player => (
                      <tr key={player.playerid}>
                        <td className="fw-semibold">{player.skaterfullname}</td>
                        <td><span className="badge bg-secondary">{player.positioncode}</span></td>
                        <td>{player.teamabbrevs}</td>
                        <td>
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            maximumFractionDigits: 0,
                          }).format(player.salary)}
                        </td>
                        <td>{player.yearleft}</td>
                        <td>{player.gamesplayed}</td>
                        <td>{player.goals}</td>
                        <td>{player.assists}</td>
                        <td>{player.points}</td>
                        <td>
                          <span className={`badge ${player.isnhl ? 'bg-success' : 'bg-warning text-dark'}`}>
                            {player.isnhl ? 'LNH' : 'Réserve'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {playerType === 'goalies' && (
            protectedGoalies.length === 0 ? (
              <div className="text-center p-5">
                <p className="text-muted">Ce directeur général n'a aucun gardien protégé.</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Équipe</th>
                      <th>Salaire</th>
                      <th>Années</th>
                      <th>PJ</th>
                      <th>V</th>
                      <th>D</th>
                      <th>BL</th>
                      <th>%Arr.</th>
                      <th>Statut</th>
                    </tr>
                  </thead>
                  <tbody>
                    {protectedGoalies.map(goalie => (
                      <tr key={goalie.playerid}>
                        <td className="fw-semibold">{goalie.goaliefullname}</td>
                        <td>{goalie.teamabbrevs}</td>
                        <td>
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            maximumFractionDigits: 0,
                          }).format(goalie.salary)}
                        </td>
                        <td>{goalie.yearleft}</td>
                        <td>{goalie.gamesplayed}</td>
                        <td>{goalie.wins}</td>
                        <td>{goalie.losses}</td>
                        <td>{goalie.shutouts}</td>
                        <td>{typeof goalie.savepct === 'number' ? goalie.savepct.toFixed(3) : '—'}</td>
                        <td>
                          <span className={`badge ${goalie.isnhl ? 'bg-success' : 'bg-warning text-dark'}`}>
                            {goalie.isnhl ? 'LNH' : 'Réserve'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}
